import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import format from 'date-fns/format';
import snakeCase from 'lodash/snakeCase';
import { useParams, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Alert } from 'react-bootstrap';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { DATE_FORMATS } from 'globals.js';
import { INTERFACE, RECORD } from 'pathnames.js';

// FUNCTIONS
import { apiFetch, documentFetch, fileDownload, makePath, modalFunctions, sleep } from 'functions.js';
import { recountActions } from 'actions.js';

// GLOBAL COMPONENTS
import Button from 'core/tools/Button';

// MAIN COMPONENT
const DeliveredButton = ({ salesTime, customerName, stockNum, disabled }) => {
  const { recordID } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const salesDate = useMemo(() => (salesTime ? format(new Date(salesTime), DATE_FORMATS.date) : ''), [salesTime]);
  const today = useMemo(() => format(new Date(), DATE_FORMATS.date), []);

  const showErrors = useCallback(errors => {
    modalFunctions.add({
      type: 'dialog',
      title: 'Error',
      body: (
        <ul className="ps-3 text-start text-danger large">
          {errors.map((e, i) => (
            <li key={i} className={clsx(i !== errors.length - 1 && 'mb-2')}>
              {e}
            </li>
          ))}
        </ul>
      ),
      buttons: [<Button.Cancel label="Close" />],
      spacing: 'sm'
    });
  }, []);

  const handleError = useCallback(
    e => {
      const errors = e?.errors?.length ? e.errors : [];
      showErrors(errors);
    },
    [showErrors]
  );

  const handleDeliver = useCallback(
    date => {
      const params =
        date !== today
          ? {
              changesaletime: date ? 1 : 0,
              changesaletimevalue: date
            }
          : undefined;
      apiFetch({
        method: 'PUT',
        endpoint: ENDPOINTS.sales.delivered.save(recordID),
        params,
        onSuccess: async () => {
          await sleep(1000);
          dispatch(recountActions.toggle());
          navigate(makePath(INTERFACE.sales, RECORD.view, recordID), {
            state: {
              statusMessage: 'Sale status has been changed to delivered.'
            }
          });
        },
        onError: handleError,
        loadingMessage: 'Submitting Delivered',
        errorMessage: 'Unable to submit delivered. Please correct any errors.',
        successMessage: 'Sale status has been changed to delivered.'
      });
    },
    [today, recordID, handleError, dispatch, navigate]
  );

  const handlePrint = useCallback(() => {
    const args = {
      endpoint: makePath(ENDPOINTS.sales.print.recap, recordID),
      params: {
        recapType: 'Gross',
        printCleanUpForm: 0
      },
      accept: 'pdf',
      contentType: 'pdf',
      mimeType: 'application/pdf',
      loadingMessage: 'Downloading Updated Gross Recap',
      errorMessage: 'Unable to download Updated Gross Recap',
      successMessage: 'Updated Gross Recap downloaded.',
      onSuccess: ({ url }) => {
        fileDownload(url, `${stockNum}_${snakeCase(customerName)}_updated_gross_recap`, undefined, true, true);
      }
    };
    documentFetch(args);
  }, [customerName, recordID, stockNum]);

  const renderConfirmationModal = useCallback(() => {
    modalFunctions.add({
      type: 'confirmation',
      title: 'Delivery Confirmation Warning',
      body: (
        <Alert variant="warning" className="mb-0">
          You have not yet printed an updated Gross Recap after Finance. Are you sure you want to mark this vehicle as
          Delivered?
        </Alert>
      ),
      cancelButton: {
        col: { xs: 24, sm: 8, lg: 11 },
        className: 'mb-4 mb-sm-0'
      },
      continueButton: {
        label: 'Print Updated Gross Recap',
        icon: {
          use: 'print'
        },
        onClick: handlePrint,
        col: { xs: 24, sm: 16, lg: 13 }
      },
      spacing: 'sm'
    });
  }, [handlePrint]);

  const renderDeliveredDateModal = useCallback(() => {
    modalFunctions.add({
      type: 'dialog',
      title: 'Sold and delivery date do not match?',
      body: `This vehicle was sold on ${salesDate}. Would you like to add the delivery for Today (${today})?`,
      buttons: [
        <Button.Cancel
          label="Close" //
          col={{ xs: { span: 24, order: 3 }, sm: 12, lg: { span: 8, order: 1 } }}
        />,
        <Button
          label="Today"
          onClick={() => handleDeliver(today)}
          col={{ xs: { span: 24, order: 1 }, sm: 12, lg: { span: 8, order: 2 } }}
          className="mb-4 mb-lg-0"
        />,
        <Button
          label={salesDate}
          onClick={() => handleDeliver(salesDate)}
          col={{ xs: { span: 24, order: 2 }, sm: 12, lg: { span: 8, order: 3 } }}
          className="mb-4 mb-lg-0"
        />
      ],
      spacing: 'sm'
    });
  }, [handleDeliver, salesDate, today]);

  const handleClick = useCallback(() => {
    apiFetch({
      endpoint: ENDPOINTS.sales.delivered.printedGrossRecap(recordID),
      onSuccess: async d => {
        if (!d?.okToDeliver) {
          renderConfirmationModal();
          return;
        }

        if (salesDate === today) handleDeliver(today);
        else renderDeliveredDateModal();
      },
      onError: handleError,
      loadingMessage: 'Submitting Delivered',
      errorMessage: 'Unable to submit delivered. Please correct any errors.'
    });
  }, [recordID, handleError, salesDate, today, handleDeliver, renderDeliveredDateModal, renderConfirmationModal]);

  return <Button onClick={handleClick} outline fullWidth={false} label="Delivered" size="sm" disabled={disabled} />;
};

// EXPORT
export default DeliveredButton;
